import mvtHeaderLogo from './mvt-header-logo.vue';
import mvtHeaderMenuAuth from './mvt-header-menu-auth.vue';
import mvtHeaderMenuPrimary from './mvt-header-menu-primary.vue';
import mvtHeaderNotifications from './mvt-header-notifications.vue';
import mvtHeaderContact from './mvt-header-contact.vue';
import mvtSearchbox from '@/common/components/mvtsearchbox/mvt-searchbox.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { setCookie } from '../../utilities/nojq/_cookie.js';
import { lazyDeps } from '../../utilities/lazy-deps.js';

const { ref: _hdr, get: loadHeaderDeps } = lazyDeps(
    () => Promise.all([
        import(/* webpackChunkName: "gtm-deps" */ '@/common/store/mapper/index.js'),
        import(/* webpackChunkName: "gtm-deps" */ '../../../../widget/spec/searchHelper'),
    ]),
    ([mapperMod, searchMod]) => ({
        mapper: mapperMod.default,
        getSaveSearchByPath: searchMod.getSaveSearchByPath,
        getPathBySearchObj: searchMod.getPathBySearchObj,
    })
);
import { updateContext } from '@/common/utilities/nojq/_utilities.js';
import { setStorage, getStorage, removeStorage } from '../../utilities/nojq/_localstorage.js';
import { redirect } from '../../utilities/nojq/_redirect.js';

const isSSR = typeof window === 'undefined';

const propertyTypeMap = {
    'single-family': 'Homes',
    'multi-family': 'Multi-family homes',
    condos: 'Condos & townhouses',
    mobile: 'Mobile homes',
    land: 'Lots & land',
    other: 'Other property types',
};

export default {
    name: 'mvtHeader',
    components: {
        mvtHeaderLogo,
        mvtHeaderMenuAuth,
        mvtHeaderMenuPrimary,
        mvtHeaderNotifications,
        mvtHeaderContact,
        mvtSearchbox,
    },
    inject: ['$eventBus'],
    props: ['sticky', 'geoInfo', 'searchInput', 'disableSearch', 'enableRecentlySearched'],
    data() {
        return {
            expand: false,
            searchInfo: {
                text: '',
                geo: null,
                extraPath: '',
            },
            activeDropdownIndex: null,
            isMounted: false,
            _boundHandlers: null,
            localGeo: null,
        };
    },
    computed: {
        ...mapGetters('glb', ['glb', 'getSavedHomesCount', 'getSplit', 'getNearbyHomesFromLatLng', 'getAgent', 'popLeadBeforeRedirect', 'getLocalGeo']),
        ...mapGetters('msp', ['msp', 'getSchoolRequest']),
        geo() {
            return this.geoInfo || this.localGeo || {};
        },
        isMapSearchPage() {
            return this.glb.pageType === 'vmapsearch' && this.$route.name === 'vmapsearch';
        },
    },
    methods: {
        ...mapActions('glb', ['addSaveSearch', 'getSavedSearches', 'addClaimedHome', 'getClaimedHomes', 'updateFavorite', 'getFavoriteListings', 'getHousesBySearchUrl', 'fetchNearbyHomesByLatLng']),
        ...mapMutations('glb', ['updateRecentSearchList', 'updateLastSearchinfo', 'updatePrevSearchinfo']),
        /* Component Methods */
        toggleMenu(visible) {
            if (visible !== undefined) {
                this.expand = visible;
            } else {
                this.expand = !this.expand;
            }
            document.body.classList.toggle('expand', this.expand);
        },
        setActiveDropdownIndex(index) {
            if (this.activeDropdownIndex === index) {
                this.activeDropdownIndex = null;
                return;
            }
            this.activeDropdownIndex = index;
        },
        /* Initialization */
        initToastBanner() {
            if (getStorage('isToastDisplayed')) return;

            const TOAST_TIME_MS = 3 * 60 * 1000;
            const now = Date.now();
            const sessionStart = getStorage('sessionStart') || now;
            setStorage('sessionStart', sessionStart);

            const delay = Math.max(TOAST_TIME_MS - (now - sessionStart), 0);

            setTimeout(() => {
                if (!this.getSavedHomesCount && this.glb.pageType === 'vdpp' && !getStorage('isToastDisplayed')) {
                    this.$eventBus.$emit('toast-linked', {
                        message: 'Save this property to get updates on status, price changes, and open houses.',
                        iconClass: 'icon-heart-o',
                        title: 'Love this home?',
                        loc: 'right',
                        autoclose: true,
                        scrollClose: this.getSplit('movoto-dpp-redesign-CW-2515'),
                    });
                    setStorage('isToastDisplayed', true);
                }
            }, delay);
        },
        initUserData() {
            if (this.glb.user?.id) {
                this.getSavedSearches();
                this.getClaimedHomes();
                this.getFavoriteListings();
                this.displayPostReferralAgentForm();
            }

            if (!this.glb.initialed) {
                this.glb.initialed = true;
                this.$eventBus.$emit('eventbus-page-update');
            }
        },
        initResizeListener() {
            this._handleResize = () => {
                this.setActiveDropdownIndex(null);
                this.toggleMenu(false);
            };

            $(document).on(`dom.resize.${this.vnumber}`, this._handleResize);
        },
        triggerPendingGoogleLogin() {
            if (!getStorage('isGoogleLogin')) return;

            const pendingRequest = getStorage('pendingRequestName');
            if (this.glb.user.email && pendingRequest) {
                if (pendingRequest === 'savedsearch') {
                    this.$eventBus.$emit('eventbus-save-search', { trigger: this.$options.name });
                } else if (pendingRequest === 'addfavorite') {
                    this.$eventBus.$emit('eventbus-save-home');
                }
            }

            removeStorage('pendingRequestName');
            removeStorage('isGoogleLogin');
        },
        displayPostReferralAgentForm() {
            const agent = this.getAgent;
            const shown = getStorage('isAgentFeedbackFormShown');

            if (!shown && agent && agent.relationshipCreatedAt && !agent.isAgentFeedback) {
                try {
                    const daysSince = (Date.now() - new Date(agent.relationshipCreatedAt)) / 86400000;
                    if (daysSince <= 7) {
                        this.$eventBus.$emit('dialog-center', {
                            name: 'agentFeedback',
                            opt: {
                                agentId: agent.id,
                                agentName: agent.name,
                                profileUrl: agent.profilePhotoUrl,
                            },
                            trigger: this.$options.name,
                        });
                        setStorage('isAgentFeedbackFormShown', true);
                    }
                } catch (err) {
                    // silently fail
                }
            }
        },
        populateLocalGeo() {
            const localGeo = this.getLocalGeo;
            if (localGeo) {
                this.localGeo = localGeo;
            }
        },
        /* Event Handler Methods */
        registerEventBusHandlers() {
            const handlers = {
                'eventbus-signup': this.onSignupHandler,
                'eventbus-login': this.onLoginHandler,
                'eventbus-signout': this.onSignoutHandler,
                'eventbus-context-update': () => updateContext(),
                'eventbus-page-update': this.onPageUpdateHandler,
                'eventbus-search': this.onSearchHandler,
                'eventbus-search-address': this.onSearchAddressHandler,
                'eventbus-survey': this.onSurveyHandler,
                'eventbus-pop-insurance': this.onPopInsuranceHandler,
                'eventbus-claimed-home': this.onClaimedHomeHandler,
                'eventbus-save-search-default': this.onSaveSearchDefaultHandler,
                'eventbus-save-search': this.onSaveSearchHandler,
                'eventbus-save-home': this.onSaveHomeHandler,
            };
            this._boundHandlers = handlers; // Save for unbinding
            Object.entries(handlers).forEach(([event, handler]) => {
                this.$eventBus.$on(event, handler.bind(this));
            });
        },
        onSignupHandler({ trigger, lite, requestName, options = {}, propertyData } = {}) {
            const opt = {
                type: lite ? 'mvtCheckEmail' : 'mvtLogin',
                requestName,
            };

            if (options.title) opt.title = options.title;
            if (options.subTitle) opt.subTitle = options.subTitle;
            if (lite && propertyData) opt.propertyData = propertyData;
            if (lite && options.buttonText) opt.buttonText = options.buttonText;

            this.$eventBus.$emit('dialog-center', {
                name: 'mvtAuthenticate',
                opt,
                trigger: trigger || this.$options.name,
            });
        },
        onLoginHandler({ trigger } = {}) {
            this.$eventBus.$emit('dialog-center', {
                name: 'mvtAuthenticate',
                opt: { type: 'mvtEmailLogin' },
                trigger: trigger || this.$options.name,
            });
        },
        onSignoutHandler() {
            const url = `${this.glb.appUrl}logout/`;
            this.$eventBus.$emit('eventbus-context-update');
            removeStorage('googleSignInPopCloseTime');
            setStorage('holeadInfo', null);
            setStorage('hotleadSentPropertyList', []);
            setStorage('googleRedirectUrl', '');
            setStorage('autoPopAfterAuthenticated', false);
            setStorage('hotleadCount', 0);
            setTimeout(function () {
                redirect(url);
            }, 2000);
        },
        onPageUpdateHandler(opt) {
            const lastSearch = getStorage('lastSearch') || null;
            const prevSearch = getStorage('prevSearch') || null;
            const path = opt?.to?.path || window.location.pathname;

            let currentSearch = null;

            // Get currentSearch if map search page
            if (this.isMapSearchPage && !isSSR && _hdr.getSaveSearchByPath) {
                currentSearch = _hdr.getSaveSearchByPath(path);
            }

            // Case 1: We have currentSearch and lastSearch
            if (currentSearch && lastSearch) {
                const isSameDisplayText = currentSearch.displayText === lastSearch.displayText;
                const isDifferentUrl = currentSearch.url.split('@')[0] !== lastSearch.url.split('@')[0];

                if (isSameDisplayText) {
                    if (this.isMapSearchPage && isDifferentUrl) {
                        this.updateLastSearchinfo(currentSearch);
                        setStorage('lastSearch', currentSearch);
                    } else {
                        if (!this.isNewSearch) return;

                        this.updateLastSearchinfo(lastSearch);
                        this.updatePrevSearchinfo(prevSearch);
                        setStorage('lastSearch', lastSearch);
                        setStorage('prevSearch', prevSearch);
                    }
                } else {
                    this.updateLastSearchinfo(currentSearch);
                    this.updatePrevSearchinfo(lastSearch);
                    setStorage('lastSearch', currentSearch);
                    setStorage('prevSearch', lastSearch);
                }
            }

            // Case 2: Only currentSearch exists
            else if (currentSearch) {
                this.updateLastSearchinfo(currentSearch);
                this.updatePrevSearchinfo(currentSearch);
                setStorage('lastSearch', currentSearch);
                setStorage('prevSearch', currentSearch);
            }

            // Case 3: Only lastSearch exists
            else if (lastSearch) {
                this.updateLastSearchinfo(lastSearch);
                setStorage('lastSearch', lastSearch);
            }

            // Update currentSearch display text to msp input and store in search list
            if (currentSearch) {
                currentSearch.text = currentSearch.displayText;
                currentSearch.displayText = this.msp.searchInput;
                this.updateSearchList(currentSearch);
            }

            this.isNewSearch = false;
        },
        onSearchHandler(newVal) {
            if (newVal.isMlsNumber) {
                return this.doMlsNUmber(newVal);
            }
            if (newVal.isGoogleGeo) {
                this.updateSearchList(newVal);
                return this.doGoogleGeoSearch(newVal);
            }
            if (newVal.geo) {
                return this.doGeoSearch(newVal);
            }
        },
        async onSearchAddressHandler(searchInfo) {
            if (!searchInfo?.geo) return;
            await loadHeaderDeps();
            const path = _hdr.getPathBySearchObj(searchInfo.geo);
            const includeAllAddress = true;

            this.getHouseByPath(path, searchInfo.extraPath, includeAllAddress).then((result) => {
                if (searchInfo.ACForm || searchInfo.getClaimedHome) {
                    searchInfo.getFirstListing(result);
                    return;
                }

                if (result.listings) {
                    this.popResult(result, { searchText: searchInfo.text });
                    return;
                }

                const { lat, lng } = searchInfo.geo.geometry.location;
                const params = {
                    lat,
                    lng,
                    rentals: !!this.glb.rentals,
                    count: 10,
                };

                this.fetchNearbyHomesByLatLng(params).then(() => {
                    if (this.getNearbyHomesFromLatLng?.length) {
                        const nearbyList = { listings: this.getNearbyHomesFromLatLng };
                        this.popResult(nearbyList, { searchText: searchInfo.text });
                    }
                });
            });
        },
        onSurveyHandler({ text, geo, type }) {
            if (type) {
                this.getHousesByGoogleGeo({ text, geo }).then((res) => {
                    if (res && res.length > 0) {
                        var geoInfo = res[0] && res[0].geo;
                        if (geoInfo.zipcode) {
                            redirect(`${this.glb.appUrl}survey/${type}/?zipcode=${geoInfo.zipcode}`);
                            return;
                        }
                    }
                    this.showPopup([], { searchText: text });
                });
            }
        },
        onPopInsuranceHandler() {
            this.$eventBus.$emit('ga', { en: 'insurance_session', ec: 'partner', el: 'insurify', props: { link_name: 'getquotes_insurancepage' } });
            window.open('https://insurify.com/start/home/partner/ojo/?utm_source=ojo&utm_medium=home&utm_campaign=insurancepage', '_blank');
        },
        onClaimedHomeHandler(geoInfo) {
            this.onSearchAddressHandler({
                text: geoInfo.text,
                geo: geoInfo.geo,
                getClaimedHome: true,
                getFirstListing: this.getFirstListing,
            });
        },
        onSaveSearchDefaultHandler(data) {
            let baseData = this.getBaseDataForSaveSearch();
            if (!baseData) {
                return;
            }
            let input = {
                userId: data && data.user ? data.user.id : '',
                trigger: this.$options.name || data.trigger,
                userType: this.glb.pageInfo.userType,
                schoolRequest: this.getSchoolRequest,
                ...baseData,
            };
            this.addSaveSearch(input);
        },
        onSaveSearchHandler(opt) {
            let baseData = this.getBaseDataForSaveSearch();
            if (!baseData) {
                return;
            }
            let trigger = opt && opt.trigger;
            var data = {
                userId: this.glb.user.id,
                trigger: trigger || this.$options.name,
                userType: this.glb.pageInfo.userType,
                schoolRequest: this.getSchoolRequest,
                ...baseData,
            };
            let hasLogin = !!this.glb.user.id;
            this.addSaveSearch(data).then(() => {
                if (hasLogin) {
                    this.$eventBus.$emit('dialog-center', {
                        name: 'mvtSearchSaveConfirm',
                        opt: {
                            searchName: data.searchName,
                        },
                        trigger: trigger || this.$options.name,
                    });
                }
            });
        },
        onSaveHomeHandler() {
            const opt = JSON.parse(getStorage('favoriteHome'));
            if (!opt) {
                return;
            }
            this.updateFavorite(opt).then((res) => {
                if (res.status && res.status.code === 0 && opt.isFavorite) {
                    this.$eventBus.$emit('dialog-center-tip', '<span class="text-bold">Success!</span> <span class="text-medium">Home saved.</span>');
                }
                if (res.status && res.status.code === 0) {
                    setStorage('favoriteHome', '');
                }
            });
        },
        /* UI Helpers */
        popResult(result, options = { searchText: '' }) {
            var that = this;
            if (result.listings && result.listings.length) {
                if (result.listings.length === 1) {
                    let url = result.listings[0].dppUrl;
                    if (this.popLeadBeforeRedirect) {
                        this.$eventBus.$emit('eventbus-popLead-beforeRedirect', url);
                    } else {
                        redirect(url);
                    }
                } else {
                    that.showPopup(result.listings, {
                        searchText: options.searchText,
                        isMlsNumberSearch: !!(result.viewUrl && result.viewUrl.startsWith('mlsnumber-')),
                    });
                }
            } else {
                that.showPopup([], { searchText: options.searchText });
            }
        },
        showPopup(listings, options = { isMlsNumberSearch: false, searchText: '' }) {
            this.$eventBus.$emit('dialog-center', {
                name: 'mvtSearchResult',
                trigger: this.$options.name,
                opt: {
                    data: listings,
                    text: options.searchText || this.searchInfo.text,
                    isLink: true,
                    isMlsNumberSearch: options.isMlsNumberSearch,
                },
            });
        },
        /* Search Helpers */
        getBaseDataForSaveSearch() {
            if (this.glb.pageType === 'vmapsearch' && this.glb.geo && this.glb.geo.geoType !== 'STATE' && !isSSR) {
                const data = {
                    url: window.location.pathname,
                    searchName: this.glb.seo.h1.replace('Real Estate & ', ''),
                };
                if (this.msp?.filter?.propertyType?.length === 0) {
                    data.searchName = this.glb?.geo?.formatAddress || data.searchName;
                } else if (this.msp?.filter?.propertyType?.length === 1) {
                    data.searchName = propertyTypeMap[this.msp?.filter?.propertyType[0]];
                } else if (this.msp?.filter?.propertyType?.length > 1) {
                    data.searchName = 'Real estate';
                }
                return data;
            } else if (this.glb.geo && this.glb.geo.cityPath && !['vschools_base', 'vschools_state'].includes(this.glb.pageType)) {
                return {
                    url: '/' + this.geo.cityPath,
                    searchName: `${this.geo.city} ${this.geo.state}`,
                };
            } else {
                return null;
            }
        },
        updateSearchList(newVal) {
            if (!isSSR) {
                if (newVal) {
                    this.updateRecentSearchList(newVal);
                }
            }
        },
        doMlsNUmber(searchInfo) {
            this.getHouseByPath(`/mlsnumber-${searchInfo.text}/`).then((result) => {
                this.popResult(result, { searchText: searchInfo.text });
            });
        },
        async doGoogleGeoSearch(searchInfo) {
            await loadHeaderDeps();
            const { geo, text } = searchInfo;

            if (geo?.address_components) {
                const googleTypes = ['locality', 'neighborhood', 'county', 'postal_code'];
                let hasRedirect = false;

                const isGoogleGeo = geo.address_components.some((component) => {
                    const types = component.types;
                    if (types.includes('street_number')) return true;
                    if (types.some((type) => googleTypes.includes(type))) {
                        hasRedirect = true;
                        return true;
                    }
                    return false;
                });

                let path = '';

                if (!isGoogleGeo) {
                    if (!text) return;

                    // Create a sanitized path from input text
                    path = `address-${text.replace(/[^a-z0-9]/gi, '-')}`.replace(/^-+|-+$/g, '').replace(/-+/g, '-') + '/';
                }

                // Fallback to standard geo search path
                if (!path) {
                    path = _hdr.getPathBySearchObj(geo, { rentals: this.glb.rentals });
                }

                if (hasRedirect) {
                    redirect(this.glb.appUrl + path);
                    return;
                }

                return this.getHouseByPath(path, '', true).then((result) => {
                    this.popResult(result, { searchText: text });
                });
            }

            // No geo components — fallback logic
            this.showPopup([], { searchText: searchInfo.text });
            return this.doGeoSearch(searchInfo);
        },
        async doGeoSearch(searchInfo) {
            if (!searchInfo?.text || !searchInfo.geo) return;

            const { text, geo, extraPath } = searchInfo;

            // Handle Map Search separately
            if (this.isMapSearchPage) {
                this.$eventBus.$emit('eventbus-text-search', {
                    searchText: text,
                    searchSuggestion: geo,
                    extraPath,
                });
                return;
            }

            await loadHeaderDeps();

            // Enrich geo with extraPath if present
            if (extraPath) {
                geo.extraPath = extraPath;
            }

            const geoInfo = _hdr.mapper.geo(geo, this.glb.rentals);
            const basePath = this.glb.appUrl + geoInfo.geoPath;

            // Redirect logic
            if (geo.type === 'school' && geo.schoolId) {
                const schoolPath = _hdr.getPathBySearchObj(geo, {
                    rentals: this.glb.rentals,
                    schoolId: geo.schoolId,
                });
                redirect(this.glb.appUrl + schoolPath);
            } else if (extraPath) {
                redirect(basePath + extraPath);
            } else if (geoInfo.geoType === 'CITY') {
                redirect(`${basePath}@${geoInfo.lat},${geoInfo.lng}/`);
            } else {
                redirect(basePath);
            }
        },
        async getHouseByPath(path, extraPath, includeAllAddress = false) {
            await loadHeaderDeps();
            var that = this;
            let searchPayload = { path, trigger: this.$options.name };
            if (includeAllAddress) {
                searchPayload.includeAllAddress = true;
            }
            var result = await this.getHousesBySearchUrl(searchPayload);
            if (result.listings && result.listings.length) {
                result.listings = result.listings.map(function (e) {
                    var dpp = _hdr.mapper.property(e, that.glb);
                    dpp.dppUrl += extraPath || '';
                    return dpp;
                });
            }
            return result;
        },
        async getHousesByGoogleGeo(searchInfo) {
            await loadHeaderDeps();
            if (searchInfo && searchInfo.geo && searchInfo.geo) {
                let path = _hdr.getPathBySearchObj(searchInfo.geo);
                var result = await this.getHouseByPath(path, '', true);
                return result ? result.listings : [];
            }
            return [];
        },
        getFirstListing(data) {
            var that = this;
            let campaign = 'homeowner-signup';
            if (this.glb.pageType.includes('vhomeservices')) {
                campaign = 'maintenance-signup';
            }
            if (data && data.listings && data.listings.length) {
                var listing = data.listings[0];
                var address = (listing.geo && listing.geo.address) || '';
                var options = {
                    propertyId: listing.propertyId,
                    isPrOnly: !listing.listingId,
                    campaign: campaign,
                    dppPath: listing.path,
                    authOptions: {
                        subTitle: `Where would you like to receive updates about ${address}?`,
                        buttonText: 'See My Dashboard',
                    },
                    propertyData: { tnImgPath: listing.tnImgPath, photoCount: listing.photoCount, geo: listing.geo },
                };
                this.tempClaimedHomes = [listing];
                this.addClaimedHome(options).then(function (res) {
                    if (res) {
                        that.$eventBus.$emit('eventbus-add-claimedhome');
                        that.$eventBus.$emit('eventbus-reset-inputgeo');
                        setTimeout(function () {
                            that.$eventBus.$emit('dialog-center', {
                                name: 'mvtSellLead',
                                trigger: that.$options.name,
                                opt: {
                                    hotleadType: 'PostClaimHomeAlsoSelling',
                                    hotleadTitle: `Selling ${listing.geo.address}?`,
                                    propertyId: listing.propertyId,
                                    propertyData: listing,
                                    showThank: that.glb.user && that.glb.user.id,
                                    showDisclaimer: false,
                                    showSellLeadForm: true,
                                },
                            });
                        }, 10);
                    }
                    that.$eventBus.$emit('eventbus-dialog-close');
                });
                this.$eventBus.$once('eventbus-dialog-close', function () {
                    if (!that.glb.user.id) that.tempClaimedHomes = null;
                });
            } else {
                this.tempClaimedHomes = null;
                this.$eventBus.$emit('dialog-center', { name: 'mvtGeoSearchResult', trigger: this.$options.name, opt: {} });
            }
        },
    },
    watch: {
        searchInfo(newVal) {
            if (newVal) {
                this.onSearchHandler(newVal);
            }
        },
    },
    beforeMount() {
        /* Pre-populate Searchbar */
        this.searchInfo.text = this.searchInput || '';
        // Linter-related logic
        if (!isSSR) {
            const splitKey = 'movoto-cw-878-linter';
            const isOn = this.getSplit(splitKey);
            const isOff = this.getSplit(splitKey, 'off');
            if (isOn) setCookie('linter', 'on');
            else if (isOff) setCookie('linter', 'off');

            if (window.location.search.includes('linter=')) {
                this.mvtLinter = defineAsyncComponent(() =>
                    import(
                        /* webpackChunkName: "mvtlinter" */
                        '@/common/components/mvtlinter/mvt-linter.vue'
                    )
                );
            }
        }
    },
    mounted() {
        this.isMounted = true;
        this.registerEventBusHandlers();
        this.initToastBanner();
        this.initUserData();
        this.initResizeListener();
        this.triggerPendingGoogleLogin();
        if (this.enableRecentlySearched) {
            this.populateLocalGeo();
        }
    },
    beforeUnmount() {
        Object.keys(this._boundHandlers || {}).forEach((event) => {
            this.$eventBus.$off(event);
        });
        $(document).off(`dom.resize.${this.vnumber}`);
    },
};
